import { useState } from 'react'

import { api, downloadBlob } from '../api/client'
import type { ConsoleDashboard, WallMonitor, WallMonitorSettings } from '../api/types'
import { useAsync, usePolling } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime, timeAgo } from './common'
import { DataTable, type Column } from './DataTable'
import { MenuAltro } from './GestioneCruscotti'
import { etichettaSchermo, leggiSchermo, schermoDelMonitor } from './SchermoRiferimento'

/**
 * «Monitor»: i PC dietro gli schermi a parete che hanno CENSO Monitor, il
 * programma a sé (non l'agente) che mostra un cruscotto in un browser a
 * chiosco e prende ordini da qui.
 *
 * Un PC diventa un monitor solo con l'**accoppiamento**: CENSO Monitor al
 * primo avvio mostra un codice a sei cifre sullo schermo, l'amministratore
 * lo scrive qui con un nome e il cruscotto da mostrare. Da quel momento la
 * riga dice come sta (in linea, browser, schermo, rete, sistema) e porta i
 * comandi e le impostazioni: cruscotto, ricarica, schermo, orari di
 * accensione, riavvio notturno, rotazione, zoom, luminosità, fuso orario,
 * aggiornamenti, screenshot, log, riavvio o spegnimento, scollega. Il
 * programma passa ogni dieci secondi: un comando arriva entro quel tempo.
 */
const GIORNI = [1, 2, 3, 4, 5, 6, 7]
const NOMI_GIORNI = () => [t('lun'), t('mar'), t('mer'), t('gio'), t('ven'), t('sab'), t('dom')]

const IMPOSTAZIONI_VUOTE: WallMonitorSettings = {
  schedule: { enabled: false, on: '07:00', off: '20:00', days: [1, 2, 3, 4, 5] },
  reboot_at: null, rotation: 0, zoom: 1, brightness: null, timezone: null, refresh_hours: 0, auto_update: true, theme: 'auto', resolution: '',
}

function Dettaglio({ m, cruscotti, onFatto, onErrore }: {
  m: WallMonitor
  cruscotti: ConsoleDashboard[]
  onFatto: () => void
  onErrore: (e: unknown) => void
}) {
  const [s, setS] = useState<WallMonitorSettings>({ ...IMPOSTAZIONI_VUOTE, ...m.settings, schedule: { ...IMPOSTAZIONI_VUOTE.schedule!, ...(m.settings.schedule ?? {}) } })
  const [nome, setNome] = useState(m.name ?? '')
  const [dettaglio, setDettaglio] = useState(m.detail ?? '')
  const [commento, setCommento] = useState(m.comment ?? '')
  const [cruscotto, setCruscotto] = useState(m.dashboard_id ?? '')
  const [titolo, setTitolo] = useState(m.title ?? '')
  const [busy, setBusy] = useState(false)
  const [foto, setFoto] = useState<string | null>(null)
  const cap = new Set(m.state.capabilities ?? [])

  async function salva(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      await api.wallMonitorImposta(m.id, {
        name: nome.trim() || undefined, detail: dettaglio.trim(), comment: commento.trim(),
        dashboard_id: cruscotto || null, title: titolo.trim() || null, fullscreen: m.fullscreen, screen: m.screen, settings: s,
      })
      onFatto()
    } catch (err) {
      onErrore(err)
    } finally {
      setBusy(false)
    }
  }
  async function mostraFoto() {
    try {
      const blob = await downloadBlob(`/monitoring/monitors/${m.id}/screenshot`)
      setFoto(URL.createObjectURL(blob))
    } catch (err) {
      onErrore(err)
    }
  }
  const sched = s.schedule!
  const giorni = NOMI_GIORNI()

  return (
    <div style={{ padding: '8px 4px 4px' }}>
      <form className="form-dialog" onSubmit={(e) => void salva(e)}>
        <h4 className="form__sezione">{t('Chi è')}</h4>
        <div className="form-grid">
          <label className="field span-2"><span className="field__label">{t('Nome')}</span>
            <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120} required /></label>
          <label className="field span-4"><span className="field__label">{t('Dettaglio (dov’è, a cosa serve: compare sotto il nome)')}</span>
            <input type="text" value={dettaglio} onChange={(e) => setDettaglio(e.target.value)} maxLength={200} placeholder={t('es. Rianimazione, parete nord, 55 pollici')} /></label>
          <label className="field span-6"><span className="field__label">{t('Commento (note per chi se ne occupa: non arriva al monitor)')}</span>
            <textarea value={commento} onChange={(e) => setCommento(e.target.value)} rows={3} maxLength={4000} /></label>
        </div>
        <h4 className="form__sezione">{t('Cosa mostra')}</h4>
        <div className="form-grid">
          <label className="field span-3"><span className="field__label">{t('Cruscotto da mostrare')}</span>
            <select value={cruscotto} onChange={(e) => setCruscotto(e.target.value)}>
              <option value="">{t('quello del tenant (o la disposizione da parete predefinita)')}</option>
              {cruscotti.map((c) => c.id && <option key={c.id} value={c.id}>{c.name}{c.shared ? ` · ${t('del tenant')}` : ` · ${t('personale')}`}</option>)}
            </select></label>
          <label className="field span-3"><span className="field__label">{t('Titolo sul monitor')}</span>
            <input type="text" value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder={t('il nome del cruscotto')} /></label>
        </div>
        <h4 className="form__sezione">{t('Schermo e risparmio energetico')}</h4>
        <div className="form-grid">
          <label className="checkbox span-2"><input type="checkbox" checked={sched.enabled} onChange={(e) => setS({ ...s, schedule: { ...sched, enabled: e.target.checked } })} />{' '}{t('Accendi e spegni lo schermo a orario')}</label>
          <label className="field span-1"><span className="field__label">{t('Accensione')}</span>
            <input type="time" value={sched.on} onChange={(e) => setS({ ...s, schedule: { ...sched, on: e.target.value } })} /></label>
          <label className="field span-1"><span className="field__label">{t('Spegnimento')}</span>
            <input type="time" value={sched.off} onChange={(e) => setS({ ...s, schedule: { ...sched, off: e.target.value } })} /></label>
          <div className="field span-2"><span className="field__label">{t('Giorni')}</span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {GIORNI.map((g, i) => (
                <label key={g} className="checkbox"><input type="checkbox" checked={sched.days.includes(g)}
                  onChange={(e) => setS({ ...s, schedule: { ...sched, days: e.target.checked ? [...sched.days, g].sort() : sched.days.filter((x) => x !== g) } })} />{' '}{giorni[i]}</label>
              ))}
            </div></div>
          <label className="field span-2"><span className="field__label">{t('Riavvio notturno del PC (facoltativo)')}</span>
            <input type="time" value={s.reboot_at ?? ''} onChange={(e) => setS({ ...s, reboot_at: e.target.value || null })} /></label>
          <label className="field span-2"><span className="field__label">{t('Ricarica il cruscotto ogni (ore, 0 = mai)')}</span>
            <input type="number" min={0} max={168} value={s.refresh_hours ?? 0} onChange={(e) => setS({ ...s, refresh_hours: Number(e.target.value) })} /></label>
          <label className="field span-2"><span className="field__label">{t('Fuso orario (es. Europe/Rome)')}</span>
            <input type="text" value={s.timezone ?? ''} onChange={(e) => setS({ ...s, timezone: e.target.value || null })} disabled={!cap.has('timezone')} /></label>
        </div>
        <h4 className="form__sezione">{t('Immagine')}</h4>
        <div className="form-grid">
          <label className="field span-2"><span className="field__label">{t('Risoluzione dello schermo')}</span>
            {(m.state.system?.display_modes?.length ?? 0) > 0 ? (
              <select value={s.resolution ?? ''} onChange={(e) => setS({ ...s, resolution: e.target.value })} disabled={!cap.has('resolution')}>
                <option value="">{t('quella del pannello')}{m.state.system?.display_mode ? ` (${m.state.system.display_mode})` : ''}</option>
                {m.state.system!.display_modes!.map((modo) => <option key={modo} value={modo}>{modo}</option>)}
              </select>
            ) : (
              <input type="text" value={s.resolution ?? ''} onChange={(e) => setS({ ...s, resolution: e.target.value })} placeholder="1920x1080" disabled={!cap.has('resolution')} />
            )}</label>
          <label className="field span-2"><span className="field__label">{t('Rotazione')}</span>
            <select value={s.rotation ?? 0} onChange={(e) => setS({ ...s, rotation: Number(e.target.value) })} disabled={!cap.has('rotation')}>
              {[0, 90, 180, 270].map((r) => <option key={r} value={r}>{r}°</option>)}
            </select></label>
          <label className="field span-2"><span className="field__label">{t('Zoom (0,5–3)')}</span>
            <input type="number" min={0.5} max={3} step={0.05} value={s.zoom ?? 1} onChange={(e) => setS({ ...s, zoom: Number(e.target.value) })} /></label>
          <label className="field span-2"><span className="field__label">{t('Luminosità (0–100, vuoto = non toccare)')}</span>
            <input type="number" min={0} max={100} value={s.brightness ?? ''} onChange={(e) => setS({ ...s, brightness: e.target.value === '' ? null : Number(e.target.value) })} disabled={!cap.has('brightness')} /></label>
          <label className="field span-2"><span className="field__label">{t('Tema del sinottico')}</span>
            <select value={s.theme ?? 'auto'} onChange={(e) => setS({ ...s, theme: e.target.value as 'auto' | 'light' | 'dark' })}>
              <option value="auto">{t('come il browser')}</option>
              <option value="light">{t('Chiaro')}</option>
              <option value="dark">{t('Scuro')}</option>
            </select></label>
          <label className="checkbox span-4"><input type="checkbox" checked={s.auto_update !== false} onChange={(e) => setS({ ...s, auto_update: e.target.checked })} />{' '}{t('Aggiorna CENSO Monitor da solo quando il Control Plane ne ha una versione nuova')}</label>
        </div>
        <p className="field__hint">{t('Le voci spente non sono disponibili su questo monitor (lo dice lui, con le sue capacità). Gli orari seguono l’orologio del PC del monitor.')}</p>
        <div className="form-dialog__azioni">
          <button type="submit" disabled={busy}>{busy ? t('Salvo…') : t('Salva')}</button>
        </div>
      </form>

      <div className="grid grid--2" style={{ marginTop: 12, gap: 16 }}>
        <div>
          <h4 className="form__sezione">{t('Il monitor dice')}</h4>
          <dl className="kv kv--attributi">
            <dt>{t('Programma')}</dt><dd>{m.state.program_version ?? '—'}{m.state.update_error && <span className="eagle-motivo"> · {m.state.update_error}</span>}{m.state.last_update && <span className="muted"> · {t('aggiornato {q}', { q: timeAgo(m.state.last_update) })}</span>}</dd>
            <dt>{t('Sistema')}</dt><dd>{m.state.system?.os ?? '—'}{m.state.system?.uptime_s != null && <span className="muted"> · {t('acceso da {d}', { d: durata(m.state.system.uptime_s) })}</span>}</dd>
            <dt>{t('Risorse')}</dt><dd>{m.state.system?.temp_cpu != null ? `${t('CPU')} ${Number(m.state.system.temp_cpu).toFixed(0)} °C` : ''}{m.state.system?.mem_free_mb != null ? ` · ${t('RAM libera')} ${m.state.system.mem_free_mb} MB` : ''}{m.state.system?.disk_free_mb != null ? ` · ${t('disco libero')} ${Math.round(Number(m.state.system.disk_free_mb) / 1024)} GB` : ''}</dd>
            <dt>{t('Rete')}</dt><dd>{m.state.network?.ip ?? '—'}{m.state.network?.iface && <span className="muted"> ({m.state.network.iface})</span>}{m.state.network?.wifi_ssid && <> · Wi‑Fi {m.state.network.wifi_ssid}{m.state.network.wifi_signal != null && ` ${m.state.network.wifi_signal}%`}</>}</dd>
            <dt>{t('Schermo')}</dt><dd>{m.state.display ?? '—'}{m.state.next_event && <span className="muted"> · {m.state.next_event}</span>}</dd>
            <dt>{t('Link del sinottico')}</dt><dd>
              {m.link_last_used_at ? t('letto {q}', { q: timeAgo(m.link_last_used_at) }) : t('mai letto')}
              {m.link_last_ip && <span className="muted"> · {m.link_last_ip}</span>}
              {m.link_allowed_ips.length > 0 && <span className="muted"> · {t('vale da {ips}', { ips: m.link_allowed_ips.join(', ') })}</span>}
              {m.link_expires_at && <span className="muted"> · {t('si rinnova prima del {d}', { d: formatDateTime(m.link_expires_at) })}</span>}
            </dd>
            <dt>{t('Capacità')}</dt><dd className="muted">{(m.state.capabilities ?? []).join(', ') || '—'}</dd>
            {m.pending.length > 0 && <><dt>{t('In attesa')}</dt><dd>{m.pending.map((p) => p.kind).join(', ')}</dd></>}
          </dl>
        </div>
        <div>
          <h4 className="form__sezione">{t('Screenshot')}</h4>
          {m.screenshot_at ? (
            <>
              <p className="field__hint">{t('Ultimo screenshot: {q}', { q: formatDateTime(m.screenshot_at) })} <button type="button" className="linklike" onClick={() => void mostraFoto()}>{t('mostra')}</button></p>
              {foto && <img src={foto} alt={t('Screenshot del monitor')} style={{ maxWidth: '100%', borderRadius: 6, border: '1px solid var(--divider)' }} />}
            </>
          ) : <p className="field__hint">{t('Nessuno screenshot: chiedilo dal menu «⋯».')}</p>}
          {m.state.logs && (
            <>
              <h4 className="form__sezione">{t('Log del programma')}{m.state.logs_at && <span className="muted"> · {timeAgo(m.state.logs_at)}</span>}</h4>
              <pre className="codice-segreto" style={{ maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 11 }}>{m.state.logs}</pre>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function durata(secondi: number): string {
  const g = Math.floor(secondi / 86400)
  const h = Math.floor((secondi % 86400) / 3600)
  if (g > 0) return t('{g} g {h} h', { g, h })
  const m = Math.floor((secondi % 3600) / 60)
  return h > 0 ? t('{h} h {m} min', { h, m }) : t('{m} min', { m })
}

export function MonitorParete() {
  const dati = usePolling(() => api.wallMonitors(), 15_000)
  const dashboards = useAsync(() => api.dashboards('eagle'), [])
  const [codice, setCodice] = useState('')
  const [nome, setNome] = useState('')
  const [dettaglio, setDettaglio] = useState('')
  const [cruscotto, setCruscotto] = useState('')
  const [titolo, setTitolo] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [aperti, setAperti] = useState<Set<string>>(new Set())

  async function azione(chiave: string, fn: () => Promise<unknown>) {
    setBusy(chiave)
    setErrore(null)
    try {
      await fn()
      await dati.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setBusy(null)
    }
  }
  const accoppia = (e: React.FormEvent) => {
    e.preventDefault()
    void azione('pair', async () => {
      await api.wallMonitorAccoppia({ code: codice.replace(/\D/g, ''), name: nome.trim(), detail: dettaglio.trim() || null, dashboard_id: cruscotto || null, title: titolo.trim() || null })
      setCodice(''); setNome(''); setDettaglio(''); setCruscotto(''); setTitolo('')
    })
  }
  const comanda = (m: WallMonitor, kind: string, conferma?: string) => {
    if (conferma && !confirm(conferma)) return
    void azione(`${m.id}:${kind}`, () => api.wallMonitorComanda(m.id, kind))
  }
  const scollega = (m: WallMonitor) => {
    if (!confirm(t('Scollegare «{name}»? Sul suo schermo tornerà il codice di accoppiamento.', { name: m.name ?? '' }))) return
    void azione(`${m.id}:unpair`, () => api.wallMonitorScollega(m.id))
  }
  const commuta = (id: string) => setAperti((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })

  const stato = (m: WallMonitor) => {
    if (m.silent) return { tono: 'warn', testo: t('silenzioso') }
    if (m.state.error) return { tono: 'warn', testo: t('con un problema') }
    if (m.state.browser === 'running') return { tono: 'ok', testo: t('in linea') }
    return { tono: 'muted', testo: t('in linea, browser fermo') }
  }
  // The browser runs but nothing has been read through the monitor's link
  // for a while (the use is recorded at most every five minutes): the screen
  // is likely on a refusal page — the only way the console can tell.
  const linkFermo = (m: WallMonitor) => {
    if (m.silent || m.state.browser !== 'running' || !m.link_expires_at) return false
    const ultimo = m.link_last_used_at ?? m.paired_at
    return !!ultimo && Date.now() - new Date(ultimo).getTime() > 8 * 60_000
  }
  const cruscotti: ConsoleDashboard[] = dashboards.data?.dashboards ?? []
  const colonne: Column<WallMonitor>[] = [
    { key: 'name', header: t('Nome'), value: (m) => [m.name, m.detail, m.hostname].filter(Boolean).join(' '), render: (m) => (
      <><strong>{m.name}</strong>{m.detail && <><br /><small>{m.detail}</small></>}{m.hostname && <><br /><small className="muted mono">{m.hostname}</small></>}</>
    ) },
    { key: 'comment', header: t('Commento'), hidden: true, value: (m) => m.comment ?? '', render: (m) => m.comment ? <span title={m.comment}>{m.comment.length > 80 ? `${m.comment.slice(0, 80)}…` : m.comment}</span> : <span className="muted">—</span> },
    { key: 'stato', header: t('Stato'), filter: 'select', value: (m) => stato(m).testo, render: (m) => {
      const s = stato(m)
      return (
        <>
          <span className={`badge badge--${s.tono}`}>{s.testo}</span>
          {m.state.error && <><br /><small className="eagle-motivo">{m.state.error}</small></>}
          {linkFermo(m) && <><br /><small className="eagle-motivo">{t('il sinottico non legge dal suo link da {q}: se lo schermo dice che il link non vale, «Link nuovo»', { q: timeAgo(m.link_last_used_at ?? m.paired_at ?? '') })}</small></>}
        </>
      )
    } },
    { key: 'dashboard', header: t('Cruscotto mostrato'), value: (m) => m.dashboard_name ?? '', render: (m) => {
      // The screen the cockpit is designed for against the one the monitor
      // shows (resolution over zoom, turned if rotated): a mismatch is
      // where «it looked different in the editor» comes from.
      const disegnato = leggiSchermo(m.dashboard_screen)
      const reale = schermoDelMonitor(m)
      const diverso = !!disegnato && !!reale && (disegnato.w !== reale.w || disegnato.h !== reale.h)
      return (
        <>
          {m.dashboard_name ?? <span className="muted">{t('quello del tenant')}</span>}{m.title && <><br /><small className="muted">{m.title}</small></>}
          {disegnato && (
            <><br /><small className={diverso ? 'eagle-motivo' : 'muted'}>
              {t('disegnato per {s}', { s: etichettaSchermo(disegnato) })}
              {diverso && reale && <> · {t('il monitor mostra {s}', { s: etichettaSchermo(reale) })}</>}
            </small></>
          )}
        </>
      )
    } },
    { key: 'schermo', header: t('Schermo'), filter: 'select', value: (m) => m.state.screen ?? m.screen, render: (m) => {
      const v = m.state.screen ?? m.screen
      return <>{v === 'off' ? t('spento') : v === 'on' ? t('acceso') : t('non so')}{m.settings.schedule?.enabled && <><br /><small className="muted">{m.settings.schedule.on}–{m.settings.schedule.off}</small></>}</>
    } },
    { key: 'rete', header: t('Rete'), value: (m) => `${m.state.network?.ip ?? ''} ${m.state.network?.wifi_ssid ?? ''}`.trim(), render: (m) => (
      m.state.network?.ip ? <>{m.state.network.ip}{m.state.network.wifi_ssid && <><br /><small className="muted">Wi‑Fi {m.state.network.wifi_ssid}{m.state.network.wifi_signal != null && ` · ${m.state.network.wifi_signal}%`}</small></>}</> : <span className="muted">—</span>
    ) },
    { key: 'sistema', header: t('Sistema'), value: (m) => m.state.system?.os ?? m.platform ?? '', render: (m) => (
      <>{m.state.system?.os ?? m.platform ?? '—'}{m.state.system?.temp_cpu != null && <><br /><small className="muted">{Number(m.state.system.temp_cpu).toFixed(0)} °C</small></>}</>
    ) },
    { key: 'last_seen', header: t('Ultimo contatto'), type: 'date', value: (m) => m.last_seen ?? '', render: (m) => m.last_seen ? <span title={formatDateTime(m.last_seen)}>{timeAgo(m.last_seen)}</span> : '—' },
    { key: 'azioni', header: '', sort: false, filter: false, searchable: false, render: (m) => {
      // The button offers the opposite of what the screen is: what the program
      // reports when it knows, else what was last asked («Spegni» pressed, the
      // program could not tell: the button must still say «Accendi»).
      const riportato = m.state.screen === 'on' || m.state.screen === 'off' ? m.state.screen : m.screen
      const acceso = riportato !== 'off'
      const cap = new Set(m.state.capabilities ?? [])
      const altre = [
        { label: t('Riavvia il browser'), onClick: () => comanda(m, 'relaunch') },
        // The program says how it can take a screenshot (a tool, or the X
        // server read directly since 31/10/2026); an older program with no
        // tool only fails: better to say so before sending the command.
        ...((m.state.capabilities ?? []).length > 0 && !cap.has('screenshot')
          ? [{ label: t('Screenshot (il monitor non sa farlo)'), onClick: () => setErrore(new Error(t('Questo monitor non dichiara la capacità «screenshot»: non ha scrot e la sua versione di CENSO Monitor non legge lo schermo dal server X. «Aggiorna CENSO Monitor» risolve: la versione corrente lo fa senza strumenti.'))) }]
          : [{ label: t('Screenshot'), onClick: () => comanda(m, 'screenshot') }]),
        { label: t('Log del programma'), onClick: () => comanda(m, 'logs') },
        { label: t('Aggiorna CENSO Monitor'), onClick: () => comanda(m, 'update_program') },
        ...(cap.has('update_system') ? [{ label: t('Aggiorna il sistema'), onClick: () => comanda(m, 'update_system') }] : []),
        { label: t('Link nuovo'), onClick: () => comanda(m, 'rotate_link') },
        ...(m.pending.length > 0 ? [{ label: t('Annulla i comandi in attesa'), onClick: () => { if (window.confirm(t('Annullare i comandi in attesa per questo monitor? Andranno dati di nuovo.'))) void azione(`${m.id}:cancel`, () => api.wallMonitorAnnullaComandi(m.id)) } }] : []),
        { label: t('Riavvia il PC'), onClick: () => comanda(m, 'reboot', t('Riavviare il PC del monitor? Il sinottico torna da solo all’accesso.')) },
        { label: t('Spegni il PC'), onClick: () => comanda(m, 'shutdown', t('Spegnere il PC del monitor? Per riaccenderlo serve qualcuno sul posto (o Wake-on-LAN).')), pericolosa: true },
        { label: t('Scollega'), onClick: () => scollega(m), pericolosa: true },
      ]
      return (
        <span className="rowactions" style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="secondary" onClick={() => commuta(m.id)}>{aperti.has(m.id) ? t('Chiudi') : t('Impostazioni…')}</button>
          <button type="button" className="secondary" onClick={() => comanda(m, 'reload')} disabled={busy !== null}>{t('Ricarica')}</button>
          <button type="button" className="secondary" onClick={() => comanda(m, acceso ? 'screen_off' : 'screen_on')} disabled={busy !== null}>{acceso ? t('Spegni schermo') : t('Accendi schermo')}</button>
          <MenuAltro voci={altre} />
          {m.pending.length > 0 && <small className="muted">{t('{n} in attesa', { n: m.pending.length })}</small>}
        </span>
      )
    } },
  ]
  const d = dati.data

  return (
    <Card title={t('Monitor')}>
      <p className="field__hint">
        {t('I PC dietro gli schermi con CENSO Monitor: un programma a sé, non l’agente, che mostra un cruscotto in un browser a chiosco e prende ordini da qui. Un PC diventa un monitor solo con l’accoppiamento: al primo avvio CENSO Monitor mostra un codice sullo schermo, tu lo scrivi qui sotto.')}
      </p>
      <ErrorBanner error={errore ?? dati.error} />
      <div className="grid grid--2" style={{ alignItems: 'start', gap: 16 }}>
        <form className="form-dialog" onSubmit={accoppia}>
          <h4 className="form__sezione">{t('Accoppia un monitor')}</h4>
          <div className="form-grid">
            <label className="field span-2"><span className="field__label">{t('Codice sullo schermo')}</span>
              <input type="text" inputMode="numeric" value={codice} onChange={(e) => setCodice(e.target.value)} placeholder="123 456" required /></label>
            <label className="field span-2"><span className="field__label">{t('Nome')}</span>
              <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} placeholder={t('es. NOC, corridoio piano 2')} required /></label>
            <label className="field span-4"><span className="field__label">{t('Dettaglio (dov’è, a cosa serve: facoltativo)')}</span>
              <input type="text" value={dettaglio} onChange={(e) => setDettaglio(e.target.value)} maxLength={200} placeholder={t('es. Rianimazione, parete nord, 55 pollici')} /></label>
            <label className="field span-2"><span className="field__label">{t('Cruscotto da mostrare')}</span>
              <select value={cruscotto} onChange={(e) => setCruscotto(e.target.value)}>
                <option value="">{t('quello del tenant (o la disposizione da parete predefinita)')}</option>
                {cruscotti.map((c) => c.id && <option key={c.id} value={c.id}>{c.name}{c.shared ? ` · ${t('del tenant')}` : ` · ${t('personale')}`}</option>)}
              </select></label>
            <label className="field span-2"><span className="field__label">{t('Titolo sul monitor')}</span>
              <input type="text" value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder={t('il nome del cruscotto')} /></label>
          </div>
          <div className="form-dialog__azioni">
            <button type="submit" disabled={busy !== null || codice.replace(/\D/g, '').length < 4 || !nome.trim()}>{busy === 'pair' ? t('Accoppio…') : t('Accoppia')}</button>
          </div>
        </form>
        <div className="eagle-blocco">
          <strong>{t('Installare CENSO Monitor')}</strong>
          <p className="field__hint" style={{ marginTop: 6 }}>
            {t('Sul PC del monitor, con l’utente che accende lo schermo: scarica il programma, poi')}
          </p>
          <pre className="codice-segreto" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{`censo-monitor --server ${d?.server ?? window.location.origin} --install\ncenso-monitor --server ${d?.server ?? window.location.origin}`}</pre>
          <p className="field__hint">{t('La prima riga lo fa partire da solo a ogni accesso; la seconda lo avvia adesso: sullo schermo compare il codice. Serve un browser (Chrome, Chromium, Edge o Firefox).')}</p>
          {d && Object.keys(d.downloads).length > 0 ? (
            <p className="field__hint">{t('Scarica:')} {Object.entries(d.downloads).map(([p, url]) => <a key={p} href={url} style={{ marginRight: 8 }}>{p}</a>)}</p>
          ) : (
            <p className="field__hint">{t('I binari di CENSO Monitor non sono in questa installazione: serve l’immagine del Control Plane che li costruisce.')}</p>
          )}
          <strong style={{ display: 'block', marginTop: 12 }}>{t('Un PC dedicato: CENSO Monitor OS')}</strong>
          <p className="field__hint" style={{ marginTop: 6 }}>
            {t('Per un PC che fa solo il monitor c’è la ISO installabile (x86): scrivila su una chiavetta (dd su Linux e macOS, Rufus in modalità DD su Windows), avvia il PC dalla chiavetta e segui l’installazione, che chiede la rete (cablata o Wi‑Fi) e l’indirizzo di questo Control Plane; al primo avvio lo schermo mostra il codice da scrivere qui accanto. Programma e sistema si aggiornano da soli e la riga del monitor ha tutte le voci, compreso «Aggiorna il sistema».')}
          </p>
          {d?.iso ? (
            <p className="field__hint">
              <a href={d.iso.url}>{t('Scarica la ISO di installazione')}</a>
              {d.iso.name && <> · <span className="mono">{d.iso.name}</span></>}
              {d.iso.size != null && <> · {t('{n} MB', { n: Math.round(d.iso.size / 1048576) })}</>}
              {d.iso.source === 'local' && <> · {t('servita da questa installazione')}</>}
              {d.iso.sha256 && <><br /><small className="muted">{t('Impronta SHA-256')} <span className="mono">{d.iso.sha256}</span></small></>}
            </p>
          ) : (
            <p className="field__hint">{t('La ISO non è ancora collegata: chi gestisce la piattaforma ne scrive l’indirizzo in Piattaforma → CENSO Monitor OS, e il link compare qui.')}</p>
          )}
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        {dati.loading && !d ? <Spinner /> : d && d.monitors.length === 0 ? (
          <EmptyState title={t('Nessun monitor accoppiato.')} hint={t('Installa CENSO Monitor sul PC dello schermo e scrivi qui il codice che compare.')} />
        ) : d ? (
          <DataTable rows={d.monitors} columns={colonne} rowKey={(m) => m.id} conteggio={{ nome: t('monitor') }}
                     expanded={(m) => aperti.has(m.id)}
                     expand={(m) => <Dettaglio m={m} cruscotti={cruscotti} onFatto={() => { commuta(m.id); void dati.reload() }} onErrore={setErrore} />} />
        ) : null}
      </div>
    </Card>
  )
}
